import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  Button,
  IconButton,
  Modal,
  Fade,
} from '@mui/material';
import {
  Home,
  LocationOn,
  AttachMoney,
  SquareFoot,
  Bed,
  Bathtub,
  Schedule,
  Visibility,
  Phone,
  Email,
  Person,
  Star,
  Close,
  ChevronLeft,
  ChevronRight,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface PropertyData {
  id: string;
  title: string;
  description: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  propertyType: string;
  price: number;
  size: number;
  bedrooms: number;
  bathrooms: number;
  images: string[];
  features: string[];
  latitude: number;
  longitude: number;
  floor?: number;
  totalFloors?: number;
  propertyAge?: number;
  elevator?: string;
  furnished?: string;
  maidRoom?: string;
  parking?: number;
  waterTanks?: number;
}

interface AgentData {
  name: string;
  email: string;
  phone: string;
  commission?: number;
  rating?: number;
}

interface ShareInfo {
  expiresAt: string;
  viewCount: number;
  images?: string[];
}

const SharedPropertyView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
//   const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [expired, setExpired] = useState(false);
  const [property, setProperty] = useState<PropertyData | null>(null);
  const [agent, setAgent] = useState<AgentData | null>(null);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  
  const [displayImages, setDisplayImages] = useState<string[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const fetchPropertyData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!token) {
        setError('Invalid share link: token is missing');
        setLoading(false);
        return;
      }
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
      const url = `${API_URL}/share-links/public/${token}`;
      console.log('Fetching from:', url); // Debug log
      
      const response = await axios.get(url);
      
      setProperty(response.data.property);
      setAgent(response.data.agent);
      setShareInfo(response.data.shareInfo);
      const si = response.data.shareInfo as ShareInfo | null;
      const imgs = (si?.images && si.images.length > 0) ? si.images : (response.data.property?.images || []);
      setDisplayImages(imgs);
    } catch (err: any) {
      console.error('Share link error:', err); // Debug log
      if (err.response?.status === 410) {
        setExpired(true);
        setError('This link has expired. Please contact the agent for a new link.');
      } else if (err.response?.status === 404) {
        setError('Share link not found. Please check the URL and try again.');
      } else {
        setError(`Failed to load property details: ${err.message}. Please try again later.`);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPropertyData();
  }, [fetchPropertyData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatPropertyType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  };

  const getExpirationTime = () => {
    if (!shareInfo) return '';
    const expiration = new Date(shareInfo.expiresAt);
    const now = new Date();
    const diff = expiration.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleNextImage = () => {
    if (displayImages.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
    }
  };

  const handlePrevImage = () => {
    if (displayImages.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? displayImages.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f7fa',
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#1967d2', width: { xs: '40px', sm: '60px' }, height: { xs: '40px', sm: '60px' } }} />
          <Typography variant="h6" sx={{ mt: 3, color: '#666', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Loading property details...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || expired) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f7fa',
          px: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          sx={{
            maxWidth: 500,
            width: '100%',
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <ErrorIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#f44336', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {expired ? 'Link Expired' : 'Error'}
          </Typography>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
          {expired && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Shareable links expire after 2 hours for security reasons.
              Please request a new link from the property agent.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  if (!property) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa', py: { xs: 2, sm: 3, md: 4 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Header with expiration info */}
        <Paper
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 0 },
            borderRadius: 2,
            backgroundColor: '#fff3e0',
            border: '1px solid #ffb74d',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule sx={{ color: '#f57c00', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            <Typography variant="body2" fontWeight={600} color="#e65100" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              This link expires in {getExpirationTime()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#666' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {shareInfo?.viewCount} {shareInfo?.viewCount === 1 ? 'view' : 'views'}
            </Typography>
          </Box>
        </Paper>

        {/* Property Details */}
        <Paper
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Images Section */}
          {displayImages.length > 0 && (
            <Box sx={{ position: 'relative', backgroundColor: '#000' }}>
              <Box
                component="img"
                src={displayImages[currentImageIndex]}
                alt={property.title}
                sx={{
                  width: '100%',
                  height: { xs: 250, sm: 350, md: 450, lg: 500 },
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
                onClick={() => setLightboxOpen(true)}
              />
              {displayImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: { xs: 4, sm: 8, md: 16 },
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { backgroundColor: 'white' },
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                    }}
                  >
                    <ChevronLeft sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: { xs: 4, sm: 8, md: 16 },
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { backgroundColor: 'white' },
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                    }}
                  >
                    <ChevronRight sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: { xs: 8, sm: 16 },
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.3, sm: 0.5 },
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    {currentImageIndex + 1} / {displayImages.length}
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Property Info */}
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'flex-start' },
                justifyContent: 'space-between',
                gap: { xs: 2, sm: 0 },
                mb: { xs: 2, sm: 3 },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  {property.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
                  <LocationOn sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, color: '#666', mt: 0.2 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {property.address}, {property.city}, {property.country}
                    {property.postalCode && ` ${property.postalCode}`}
                  </Typography>
                </Box>
              </Box>
              <Chip
                icon={<Home sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />}
                label={formatPropertyType(property.propertyType)}
                color="primary"
                sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Key Details */}
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
              <Grid item xs={6} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  <AttachMoney sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, color: '#1967d2', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h6" fontWeight={700} color="primary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}>
                    {formatCurrency(property.price)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}>
                    Price
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  <SquareFoot sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, color: '#1967d2', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}>
                    {property.size} m²
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}>
                    Area
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  <Bed sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, color: '#1967d2', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}>
                    {property.bedrooms}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}>
                    Bedrooms
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  <Bathtub sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, color: '#1967d2', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}>
                    {property.bathrooms}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}>
                    Bathrooms
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Additional Details */}
            {(property.floor != null || property.totalFloors != null || property.propertyAge != null ||
              property.elevator || property.furnished || property.maidRoom ||
              property.parking != null || property.waterTanks != null) && (
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Additional Details
                </Typography>
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {property.floor != null && (
                    <Grid item xs={6} sm={4} md={3}>
                      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700}>{property.floor}</Typography>
                        <Typography variant="caption" color="text.secondary">Floor</Typography>
                      </Box>
                    </Grid>
                  )}
                  {property.totalFloors != null && (
                    <Grid item xs={6} sm={4} md={3}>
                      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700}>{property.totalFloors}</Typography>
                        <Typography variant="caption" color="text.secondary">Total Floors</Typography>
                      </Box>
                    </Grid>
                  )}
                  {property.propertyAge != null && (
                    <Grid item xs={6} sm={4} md={3}>
                      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700}>{property.propertyAge} yrs</Typography>
                        <Typography variant="caption" color="text.secondary">Property Age</Typography>
                      </Box>
                    </Grid>
                  )}
                  {property.elevator && (
                    <Grid item xs={6} sm={4} md={3}>
                      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>{property.elevator}</Typography>
                        <Typography variant="caption" color="text.secondary">Elevator</Typography>
                      </Box>
                    </Grid>
                  )}
                  {property.furnished && (
                    <Grid item xs={6} sm={4} md={3}>
                      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>{property.furnished}</Typography>
                        <Typography variant="caption" color="text.secondary">Furnished</Typography>
                      </Box>
                    </Grid>
                  )}
                  {property.maidRoom && (
                    <Grid item xs={6} sm={4} md={3}>
                      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>{property.maidRoom}</Typography>
                        <Typography variant="caption" color="text.secondary">Maid Room</Typography>
                      </Box>
                    </Grid>
                  )}
                  {property.parking != null && (
                    <Grid item xs={6} sm={4} md={3}>
                      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700}>{property.parking}</Typography>
                        <Typography variant="caption" color="text.secondary">Parking Spots</Typography>
                      </Box>
                    </Grid>
                  )}
                  {property.waterTanks != null && (
                    <Grid item xs={6} sm={4} md={3}>
                      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700}>{property.waterTanks} L</Typography>
                        <Typography variant="caption" color="text.secondary">Water Tanks</Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Description */}
            {property.description && (
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Description
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {property.description}
                </Typography>
              </Box>
            )}

            {/* Features */}
            {property.features.length > 0 && (
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Features
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 } }}>
                  {property.features.map((feature, index) => (
                    <Chip
                      key={index}
                      label={feature}
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Agent Contact */}
            {agent && (
              <Paper
                sx={{
                  p: { xs: 2, sm: 3 },
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="h6" fontWeight={600} sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Contact Agent
                </Typography>
                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                      <Person sx={{ color: '#1967d2', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          Agent Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {agent.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                      <Email sx={{ color: '#1967d2', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          Email
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, wordBreak: 'break-word' }}>
                          {agent.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                      <Phone sx={{ color: '#1967d2', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          Phone
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {agent.phone || 'Not provided'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  {agent.rating !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                        <Star sx={{ color: '#ffa726', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Rating
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {agent.rating.toFixed(1)} / 5.0
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                <Button
                  variant="contained"
                  fullWidth
                  href={`mailto:${agent.email}?subject=Inquiry about ${property.title}`}
                  sx={{
                    mt: { xs: 2, sm: 3 },
                    py: { xs: 1.2, sm: 1.5 },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    backgroundColor: '#1967d2',
                    boxShadow: '0 4px 12px rgba(25, 103, 210, 0.3)',
                    '&:hover': {
                      backgroundColor: '#1557b0',
                      boxShadow: '0 6px 16px rgba(25, 103, 210, 0.4)',
                    },
                  }}
                >
                  Send Inquiry
                </Button>
              </Paper>
            )}
          </Box>
        </Paper>

        {/* Footer Note */}
        <Paper
          sx={{
            mt: { xs: 2, sm: 3 },
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            This is a temporary shareable link. For security reasons, it will expire automatically.
            Contact the agent directly for more information or to schedule a viewing.
          </Typography>
        </Paper>
      </Container>

      {/* Lightbox Modal */}
      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        closeAfterTransition
      >
        <Fade in={lightboxOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '100vw',
              maxHeight: '100vh',
              width: '100vw',
              height: '100vh',
              outline: 'none',
              backgroundColor: 'rgba(0,0,0,0.92)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => setLightboxOpen(false)}
          >
            <Box
              sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button — always visible inside the image area */}
              <IconButton
                onClick={() => setLightboxOpen(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.55)',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                }}
              >
                <Close />
              </IconButton>

              <Box
                component="img"
                src={displayImages[currentImageIndex]}
                alt={property.title}
                sx={{
                  maxWidth: '95vw',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: 1,
                  display: 'block',
                }}
              />

              {/* Image counter */}
              {displayImages.length > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    px: 2,
                    py: 0.4,
                    borderRadius: 2,
                    fontSize: '0.85rem',
                    pointerEvents: 'none',
                  }}
                >
                  {currentImageIndex + 1} / {displayImages.length}
                </Box>
              )}

              {displayImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: { xs: 4, sm: 12 },
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.55)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                      width: { xs: 36, sm: 44 },
                      height: { xs: 36, sm: 44 },
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: { xs: 4, sm: 12 },
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.55)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                      width: { xs: 36, sm: 44 },
                      height: { xs: 36, sm: 44 },
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default SharedPropertyView;
